import {useCallback, useEffect, useRef, useState} from 'react';

import {MinimalFolder, PagedMinimalFolder} from 'constants/FolderConstants';
import {IntentLevel} from 'file-browser/FileSelectorConstants';
import FolderDAO, {FetchPagedResponse} from 'file-browser/FolderDAO';
import notifications from 'notifications';
import {LoadMorePromise} from 'ui-library/select-tree/SelectTree';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';

import * as FileSelectorUtil from './FileSelectorUtil';

const PAGE_SIZE = 50;

export interface PaginatedFolderOptions {
  /** Permission level required for every returned folder. */
  level: IntentLevel;
  /** Receives all folders loaded so far for modal validation and folder creation. */
  onFoldersLoaded: (folders: Array<MinimalFolder>) => void;
  /** Whether inventory folders are shown in the picker. */
  showInventory: boolean;
  /** Whether notebook folders are shown in the picker. */
  showNotebook: boolean;
}

export interface PaginatedFolderTreeState {
  /** Loaded folders that have children and therefore render with a caret. */
  items: Array<TreeItem>;
  /** Loaded folders known to have no children. */
  baseLeafItems: Array<TreeItem>;
  /** Whether a folder loaded under an expanded parent should render with a caret. */
  isLoadedItemExpandable: (item: TreeItem) => boolean;
  /** Whether a top-level page request is in flight. Child loading state is owned by SelectTree. */
  isLoadingRootItems: boolean;
  /** Load one child page. SelectTree owns the accumulated child items and their offsets. */
  loadMore: (parentId: string, offset: number) => LoadMorePromise;
  /** Whether the server supplied another top-level page offset. */
  moreRootItems: boolean;
  /** Request the next top-level page, if one is available. */
  onLoadMoreRootItems?: () => void;
  syncBrowsePropsFromCache: () => void;
}

function getVisibleTreeItemsFromCache(
  foldersById: Map<string, PagedMinimalFolder>,
  level: IntentLevel,
  showInventory: boolean,
  showNotebook: boolean
): Array<TreeItem> {
  const isVisible = FileSelectorUtil.folderPickerPredicate({level, showInventory, showNotebook});
  const allFolders = [...foldersById.values()];
  return FileSelectorUtil.foldersToSelectTreeItems(allFolders.filter((folder) => isVisible(folder)));
}

/** Merge an API page into the cache and reconnect every folder to its cached parent. */
function mergeFolderPage(
  foldersById: Map<string, PagedMinimalFolder>,
  pageFolders: Array<PagedMinimalFolder>
): {allFolders: Array<PagedMinimalFolder>; pageFolderIds: ReadonlySet<string>} {
  for (const folder of pageFolders) {
    foldersById.set(folder.api_identifier, {...folder, parent: null});
  }
  // A later page can replace a parent object, so refresh parent references across the entire cache.
  for (const folder of foldersById.values()) {
    folder.parent = folder.parentId ? (foldersById.get(folder.parentId) ?? null) : null;
  }
  return {
    allFolders: [...foldersById.values()],
    pageFolderIds: new Set(pageFolders.map((folder) => folder.api_identifier)),
  };
}

/**
 * Adapt the paged folder API to SelectTree's existing lazy-loading contract.
 *
 * The hook owns the cumulative folder cache, while SelectTree owns pagination state for each expanded folder.
 * FolderSelectTree handles search separately because typeahead still filters the complete folder list on the
 * client. Loaded child items remain in these browse props so returning from search restores them while the
 * remounted browse tree safely restarts its offsets.
 *
 * Request flow:
 * - When enabled, the hook clears its state and requests the first top-level page.
 * - Expanding a folder makes SelectTree request that folder's first child page.
 * - If either level has another page, its loading indicator requests that page when it enters the viewport.
 * - The hook accumulates every loaded item; SelectTree also accumulates child pages with their pagination state.
 * - Every response is added to the shared folder cache before its new items are returned to the caller.
 */
export default function usePaginatedFolderTree(
  options: PaginatedFolderOptions | undefined
): PaginatedFolderTreeState {
  const enabled = options != null;
  const level = options?.level ?? 'read';
  const onFoldersLoaded = options?.onFoldersLoaded;
  const showInventory = options?.showInventory ?? false;
  const showNotebook = options?.showNotebook ?? false;
  // Treat this callback as an output: use its latest identity without restarting top-level pagination.
  const onFoldersLoadedRef = useRef(onFoldersLoaded);
  onFoldersLoadedRef.current = onFoldersLoaded;
  // One cache supplies parent links to the tree and every loaded folder to the modal's permission checks.
  const foldersById = useRef(new Map<string, PagedMinimalFolder>());
  const [loadedItems, setLoadedItems] = useState<Array<TreeItem>>([]);
  const [nextTopLevelOffset, setNextTopLevelOffset] = useState<number | null>(0);
  const [isLoadingTopLevel, setIsLoadingTopLevel] = useState(false);
  const [topLevelLoadFailed, setTopLevelLoadFailed] = useState(false);

  const syncBrowsePropsFromCache = useCallback((): void => {
    setLoadedItems(getVisibleTreeItemsFromCache(foldersById.current, level, showInventory, showNotebook));
  }, [level, showInventory, showNotebook]);

  /**
   * Add one server response to the shared cache and return only its new items in SelectTree's format.
   *
   * Building from the full cache keeps each new item attached to a parent loaded by an earlier request. The
   * modal also receives the full cache for validation, while SelectTree receives only this response because it
   * already retains previously loaded top-level and child pages.
   */
  const cachePageAndBuildTreeItems = useCallback(
    (response: FetchPagedResponse): Array<TreeItem> => {
      const {allFolders, pageFolderIds} = mergeFolderPage(foldersById.current, response.items);
      onFoldersLoadedRef.current?.(allFolders);

      const allVisibleItems = getVisibleTreeItemsFromCache(
        foldersById.current,
        level,
        showInventory,
        showNotebook
      );
      setLoadedItems(allVisibleItems);
      return allVisibleItems.filter((item) => pageFolderIds.has(item.id));
    },
    [level, showInventory, showNotebook]
  );

  const requestPage = useCallback(
    (params: {parentId?: string; offset?: number}): Promise<FetchPagedResponse> =>
      FolderDAO.fetchPaged({
        ...params,
        level,
        pageSize: PAGE_SIZE,
      }),
    [level]
  );

  const loadTopLevelPage = useCallback(
    async (offset: number): Promise<void> => {
      setIsLoadingTopLevel(true);
      try {
        const response = await requestPage({offset});
        cachePageAndBuildTreeItems(response);
        setNextTopLevelOffset(response.nextOffset);
      } catch (error) {
        // Stop automatic pagination after one failure. Reopening the picker starts a fresh request.
        setTopLevelLoadFailed(true);
        throw error;
      } finally {
        setIsLoadingTopLevel(false);
      }
    },
    [cachePageAndBuildTreeItems, requestPage]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    foldersById.current.clear();
    setLoadedItems([]);
    setNextTopLevelOffset(0);
    setTopLevelLoadFailed(false);
    loadTopLevelPage(0).catch((error) => {
      notifications.notifyError(error);
    });
  }, [enabled, loadTopLevelPage]);

  const loadChildPage = useCallback(
    async (parentId: string, offset: number): LoadMorePromise => {
      const response = await requestPage({parentId, offset});
      return {
        items: cachePageAndBuildTreeItems(response),
        more: response.nextOffset != null,
        // `offset` identifies this response so SelectTree can discard stale pages. `nextOffset` remains
        // server-owned because client-side folder filtering can reduce the number of rendered items.
        offset,
        nextOffset: response.nextOffset ?? offset + response.items.length,
      };
    },
    [cachePageAndBuildTreeItems, requestPage]
  );

  const hasChildren = useCallback(
    (item: TreeItem): boolean => foldersById.current.get(item.id)?.hasChildren ?? false,
    []
  );
  return {
    items: loadedItems.filter((item) => hasChildren(item)),
    baseLeafItems: loadedItems.filter((item) => !hasChildren(item)),
    isLoadedItemExpandable: hasChildren,
    loadMore: loadChildPage,
    moreRootItems: enabled && !topLevelLoadFailed && nextTopLevelOffset != null,
    isLoadingRootItems: isLoadingTopLevel,
    onLoadMoreRootItems: topLevelLoadFailed
      ? undefined
      : () => {
          if (nextTopLevelOffset != null) {
            loadTopLevelPage(nextTopLevelOffset).catch((error) => {
              notifications.notifyError(error);
            });
          }
        },
    syncBrowsePropsFromCache,
  };
}
